import { makeStyles } from "@material-ui/core/styles";
import { Exercises } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";
import Popup from "reactjs-popup";

import { useStateValue } from "../../../config/AppContext";
import { ExerciseFocus, ExerciseTypesEnum } from "../../../types/StaticTypes";
import { getSuperSetFormDataInputElement } from "../../Modals/SuperSetForm";
import ExerciseFilter, { FilterTabs, FilterValue } from "./ExerciseFilter";
import ExerciseListItem from "./ExerciseListItem";
import filterExercises from "./filterExercises";
import SelectExerciseListItem, { SelectedExercises } from "./SelectExerciseListItem";

const useStyles = makeStyles((theme) => ({
  toolBar: {
    display: "flex",
    justifyContent: "space-between",
    paddingLeft: 20,
    paddingRight: 5,
  },
  generateFocusListLi: {
    textTransform: "capitalize",
    cursor: "pointer",
    paddingTop: 1,
    padding: "4px 5px",
    borderRadius: "5px",
  },
  generateFocusListLiWithoutPadding: {
    textTransform: "capitalize",
    cursor: "pointer",
    padding: "4px 5px",
    borderRadius: "5px",
  },
  checkFocusHead: {
    cursor: "pointer",
    display: "inline-block",
    // backgroundColor: 'var(--primary)',
    borderRadius: "5px",
    padding: "5px",
    textAlign: "left",
    color: "var(--text-primary)",
    lineHeight: "10px",
    float: "right",
  },
}));

const customStyles: any = {
  checkFocusHeadPopup: {
    border: "1px solid rgb(239 239 239)",
    width: 220,
    margin: "20px 0 0 0",
    textAlign: "left",
    borderRadius: "5px",
    zIndex: 1,
    padding: 10,
    fontSize: 14,
  },
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

export const ExerciseList: React.FC = (props: any) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const currentPath: string = props.location && props.location.pathname.replace("/", "");

  const { catalog } = useStateValue();

  const [filterValue, setFilterValue] = React.useState<FilterValue>({
    searchQuery: "",
    exerciseType: null,
    tab: FilterTabs.ALL_EXERCISES,
  });
  const [focusFilter, setFocusFilter] = React.useState<
    { focus: ExerciseFocus; type: ExerciseTypesEnum }[] | null
  >();
  const [selected, setSelected] = React.useState<SelectedExercises>(null);

  const handleExerciseFocusFilterChange = React.useCallback(
    (e: React.MouseEvent) => {
      const focus = e.currentTarget.innerHTML as unknown as ExerciseFocus;
      const type = e.currentTarget.getAttribute("type-name") as unknown as ExerciseTypesEnum;

      // If focus is all then set focusFilter to null
      if (/all/i.test(focus.toString())) return setFocusFilter(null);

      // If focusFilter is falsy or selected focus is not 'all' then set focus filter

      if (!focusFilter || focusFilter.length === 0 || !/all/i.test(focus.toString())) {
        return setFocusFilter([{ focus, type }]);
      }

      let newFilter = focusFilter;
      // If focus is all then set focusFilter to null
      if (/all/i.test(focus.toString())) return setFocusFilter(null);
      const targetHead = document.getElementById(`${type}`);

      if (targetHead) {
        targetHead.scrollIntoView();
      }
      const targetFilter = newFilter.find((el) => el.type === type);

      // If type doesn't exist in focusFilter array then add to newFilter
      if (!targetFilter) {
        newFilter = [...newFilter, { focus, type }];
        return setFocusFilter(newFilter);
      }
      // If type exists then find type and replace entry
      if (targetFilter) {
        const index = newFilter.indexOf(targetFilter);
        newFilter[index] = { focus, type };
        return setFocusFilter(newFilter);
      }
    },
    [focusFilter]
  );

  const doesExerciseMatchFocusFilter = React.useCallback(
    (exercise: Exercises.IExercise) => {
      const focusFilterForExerciseType = focusFilter?.find(
        (focusFilterItem) => focusFilterItem.type === exercise.type
      );

      // if there is no focus filter for this exercise type, we just show all exercises of this type
      if (!focusFilterForExerciseType) {
        return true;
      }

      // if there is a focus filter for this exercise type, show only matching exercises
      return (
        exercise.focus_prim &&
        exercise.focus_prim.toLowerCase() === focusFilterForExerciseType.focus
      );
    },
    [focusFilter]
  );

  const generateExerciseItem = React.useCallback(
    (exercise: Exercises.IExercise) => {
      if (!doesExerciseMatchFocusFilter(exercise)) {
        return <div key={exercise.id}></div>;
      }

      // For pages other than "/exercises", this list is for selecting exercises to add to a session.
      if (!/exercises/.test(currentPath)) {
        return (
          <SelectExerciseListItem
            key={exercise.id}
            exercise={exercise}
            selected={selected}
            onSelectionChange={setSelected}
          />
        );
      }

      // For the "/exercises" page, this list shows exercise details on click.
      const handleExerciseListItemClick = ({ id, name }: Exercises.IExercise) =>
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
          ? props.history.push(`/exercises/${id}`)
          : props.setExedrawers({ id: id, name: name, state: true });

      return (
        <ExerciseListItem
          key={exercise.id}
          exercise={exercise}
          onClick={handleExerciseListItemClick}
        />
      );
    },
    [props, currentPath, selected, doesExerciseMatchFocusFilter]
  );

  const handleFilterChange = (newFilterValue: FilterValue) => {
    setFilterValue(newFilterValue);
  };

  const filteredExercises = React.useMemo(
    () => filterExercises(catalog, filterValue),
    [catalog, filterValue]
  );

  const generateExercisesList = React.useCallback(() => {
    const generateFocusList = (head: ExerciseTypesEnum, close: () => void) => {
      let Focus: React.ReactElement[] = [];
      for (let i = 0; i < 10; i++) {
        const checkFocusFilter =
          focusFilter &&
          focusFilter.find((filter: { type: ExerciseTypesEnum; focus: ExerciseFocus }) => {
            return (
              filter.type === head && filter.focus.toString() === ExerciseFocus[i].toLowerCase()
            );
          });

        Focus = [
          ...Focus,
          <li
            key={i}
            type-name={`${head}`}
            style={{
              // backgroundColor: checkFocusFilter
              //   ? 'var(--primary)'
              //   : 'var(--white)',
              color: checkFocusFilter ? "var(--text-primary)" : "var(--light-dark)",
            }}
            onClick={(e) => {
              handleExerciseFocusFilterChange(e);
              close();
            }}
            onMouseEnter={(e) => {
              if (checkFocusFilter) return;
              // e.currentTarget.style.backgroundColor = 'var(--primary)';
              e.currentTarget.style.color = "var(--text-primary)";
            }}
            onMouseLeave={(e) => {
              if (checkFocusFilter) return;
              // e.currentTarget.style.backgroundColor = 'var(--white)';
              e.currentTarget.style.color = "var(--light-dark)";
            }}
            className={classes.generateFocusListLiWithoutPadding}
          >
            {ExerciseFocus[i].toLowerCase()}
          </li>,
        ];
      }
      // console.log('focusFilter', focusFilter, Focus)
      const checkFocusType =
        !focusFilter ||
        (focusFilter &&
          !focusFilter.find((filter: { type: ExerciseTypesEnum }) => filter.type === head));
      Focus = [
        <li
          key={11}
          type-name={`${ExerciseTypesEnum[head]}`}
          style={{
            // backgroundColor: checkFocusType ? 'var(--primary)' : 'var(--white)',
            color: checkFocusType ? "var(--text-primary)" : "var(--light-dark)",
          }}
          onClick={(e) => {
            handleExerciseFocusFilterChange(e);
            close();
          }}
          onMouseEnter={(e) => {
            if (checkFocusType) return;
            // e.currentTarget.style.backgroundColor = 'var(--primary)';
            e.currentTarget.style.color = "var(--text-primary)";
          }}
          onMouseLeave={(e) => {
            if (checkFocusType) return;
            // e.currentTarget.style.backgroundColor = 'var(--white)';
            e.currentTarget.style.color = "var(--light-dark)";
          }}
          className={classes.generateFocusListLiWithoutPadding}
        >
          {t("All")}
        </li>,
        ...Focus,
      ];
      return Focus;
    };
    const mapExercisesHead = (accum: Array<any>, exercise: Exercises.IExercise, i: number) => {
      const head = exercise.type;
      if (accum.find((el: React.ReactElement) => el.props.id === head)) {
        accum = [...accum, generateExerciseItem(exercise)];
        return accum;
      }
      const checkFocusHead =
        focusFilter &&
        focusFilter.find((el: { type: ExerciseTypesEnum }) => {
          return el.type === head;
        });
      accum = [
        ...accum,
        <h5
          id={`${head}`}
          key={i + 100}
          className="section-heading"
          style={{ fontSize: 14, padding: "0.5rem 0" }}
        >
          {catalog.typesDisplay(`${head}`)}
          {
            <Popup
              trigger={
                <span className={classes.checkFocusHead}>
                  {t("FILTER BY")}:{" "}
                  {checkFocusHead ? catalog.typesDisplay(checkFocusHead.focus) : t("All")}
                </span>
              }
              position="bottom center"
              closeOnDocumentClick
              closeOnEscape
              contentStyle={customStyles.checkFocusHeadPopup}
              arrowStyle={{ left: 200 }}
            >
              {(close) => <ul style={{ fontWeight: 400 }}>{generateFocusList(head, close)}</ul>}
            </Popup>
          }
        </h5>,
      ];
      accum = [...accum, generateExerciseItem(exercise)];
      return accum;
    };

    const exercisesMap = filteredExercises.reduce(mapExercisesHead, []);

    return (
      <div style={{ padding: 0 }} className="container">
        {exercisesMap}
      </div>
    );
  }, [
    catalog,
    filteredExercises,
    focusFilter,
    handleExerciseFocusFilterChange,
    generateExerciseItem,
    t,
    classes.checkFocusHead,
    classes.generateFocusListLiWithoutPadding,
  ]);

  React.useEffect(() => {
    const superSetInputElem = getSuperSetFormDataInputElement();
    if (!/exercises/.test(currentPath) && superSetInputElem && !selected)
      return setSelected(JSON.parse(superSetInputElem.value));
  }, [selected, currentPath]);

  return (
    <div style={{ display: "flex", justifyContent: "space-around" }}>
      <div
        id="exercises-listing"
        style={{
          width:
            /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
            !/exercises/.test(window.location.href)
              ? "100%"
              : "60%",
        }}
      >
        <ExerciseFilter filterValue={filterValue} onChange={handleFilterChange} />
        <TabPanel value={filterValue.tab} index={0}>
          <section>{generateExercisesList()}</section>
        </TabPanel>
        <TabPanel value={filterValue.tab} index={1}>
          <section>{generateExercisesList()}</section>
        </TabPanel>
      </div>
      {props.children}
    </div>
  );
};
